import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    Heart, ArrowLeft, MapPin, Clock, Share, Trash, Sparkle, ForkKnife, Tree, MusicNote, Confetti, Wine, Sun,
    ArrowsClockwise, PencilSimple, CalendarPlus, MapTrifold, Suitcase, WarningCircle, Ticket, Star,
} from "@phosphor-icons/react";
import { travelDateApi, authApi } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { PassportLogo } from "@/components/Reveal";
import { TierBadge } from "@/pages/TravelDate";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { usePassportShare } from "@/components/messages/PassportShareProvider";

export const CORAL = "#F4A28F";

export const KIND_ICON = {
    dining: ForkKnife, park: Tree, entertainment: Confetti, drinks: Wine, nightlife: Wine, activity: Sparkle, default: Sun,
};

export default function TravelDateDetail() {
    const { id } = useParams();
    const { user, setUser, loading } = useAuth();
    const { openPassportShare } = usePassportShare();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [d, setD] = useState(null);
    const [busy, setBusy] = useState(false);
    const [shareOpen, setShareOpen] = useState(false);

    const load = useCallback(() => travelDateApi.get(id).then(setD).catch(() => { toast.error(t("travelDateDetail.toasts.notFound")); navigate("/traveldate"); }), [id, navigate, t]);
    useEffect(() => { if (user) load(); }, [user, load]);

    // Poll while status === "composing" — the background LLM writes to the doc when done
    const status = d?.status;
    useEffect(() => {
        if (status !== "composing") return;
        const timer = setInterval(async () => {
            try {
                const fresh = await travelDateApi.get(id);
                setD(fresh);
                if (fresh.status !== "composing") clearInterval(timer);
            } catch { /* keep polling */ }
        }, 2500);
        return () => clearInterval(timer);
    }, [status, id]);

    if (loading || !user) return <div className="min-h-screen grid place-items-center text-white/60">{t("common.loading")}</div>;
    if (!d) return <div className="min-h-screen grid place-items-center text-white/60">{t("common.loading")}</div>;

    if (d.status === "composing") return <ComposingScreen city={d.city} />;
    if (d.status === "failed")   return <FailedScreen msg={d.error} onRetry={async () => {
        try {
            const r = await travelDateApi.regenerate(id);
            navigate(`/traveldate/${r.id}`);
        } catch (e) { toast.error(e?.response?.data?.detail || t("travelDateDetail.toasts.errRetry")); }
    }} />;

    const toggleFav = async () => { const r = await travelDateApi.toggleFavorite(id); setD({ ...d, favorited: r.favorited }); };
    const setStatus = async (s) => {
        const res = await travelDateApi.setStatus(id, s);
        toast.success(t("travelDateDetail.toasts.markedStatus", { status: s }));
        // Update user context with fresh stats from backend (miles, world%, countries)
        if (res?.user_stats && setUser) {
            setUser((prev) => ({ ...prev, ...res.user_stats }));
        }
        // Tell Dashboard to reload stamps so map, routes, live all update instantly
        window.dispatchEvent(new CustomEvent("atlas:refresh"));
        load();
    };
    const remove = async () => {
        try {
            await travelDateApi.remove(id);
            toast.success(t("travelDateDetail.toasts.deleted"));
            if (d?.status === "completed") {
                authApi.me().then(setUser).catch(() => {});
                window.dispatchEvent(new CustomEvent("atlas:refresh"));
            }
            navigate("/traveldate/history");
        } catch { toast.error(t("travelDateDetail.toasts.errDelete", "Failed to delete")); }
    };

    const shareInMessages = () => {
        if (!d?.id) return;
        openPassportShare({ shareRef: { entity_type: "traveldate", entity_id: d.id }, sourceLabel: "this TravelDate" });
    };
    const addToTrip = async () => {
        setBusy(true);
        try {
            const r = await travelDateApi.addToTrip(id, {});
            toast.success(t("travelDateDetail.toasts.addedToTrip"));
            navigate(`/trip/${r.trip_id}`);
        } catch (e) { toast.error(e?.response?.data?.detail || t("travelDateDetail.toasts.errAddToTrip")); }
        finally { setBusy(false); }
    };

    const regenerate = async () => {
        setBusy(true);
        try {
            const created = await travelDateApi.regenerate(id);
            toast.success(t("travelDateDetail.toasts.regenerated"));
            navigate(`/traveldate/${created.id}`);
        } catch (e) { toast.error(e?.response?.data?.detail || t("travelDateDetail.toasts.errRegenerate")); }
        finally { setBusy(false); }
    };

    const editPreferences = () => {
        navigate("/traveldate", { state: { editPrefs: d.preferences || null } });
    };

    const addToCalendar = () => {
        // Build a minimal ICS file for the itinerary using today's date + parsed times
        const pad = (n) => String(n).padStart(2, "0");
        const fmt = (dt) => `${dt.getUTCFullYear()}${pad(dt.getUTCMonth() + 1)}${pad(dt.getUTCDate())}T${pad(dt.getUTCHours())}${pad(dt.getUTCMinutes())}00Z`;
        const parseTime = (timeStr) => {
            const m = /(\d{1,2}):(\d{2})\s*(AM|PM)?/i.exec(timeStr || "");
            if (!m) return null;
            let h = parseInt(m[1], 10); const min = parseInt(m[2], 10); const suf = (m[3] || "").toUpperCase();
            if (suf === "PM" && h < 12) h += 12; if (suf === "AM" && h === 12) h = 0;
            return { h, min };
        };
        const today = new Date();
        const events = (d.itinerary || []).map((s, i) => {
            const timeObj = parseTime(s.time);
            if (!timeObj) return null;
            const start = new Date(today); start.setHours(timeObj.h, timeObj.min, 0, 0);
            if ((s.day || 1) === 2) start.setDate(start.getDate() + 1);
            const end = new Date(start.getTime() + (s.duration_minutes || 60) * 60000);
            const ev = s.event;
            const location = ev ? [ev.venue, ev.address, ev.city, ev.country].filter(Boolean).join(", ")
                                : `${d.city}${d.country ? ", " + d.country : ""}`;
            const descParts = [s.subtitle || d.title];
            if (ev) {
                if (ev.description)    descParts.push(ev.description);
                if (ev.price_display)  descParts.push(`Price: ${ev.price_display}`);
                if (ev.ticket_url)     descParts.push(`Tickets: ${ev.ticket_url}`);
                if (ev.disclaimer)     descParts.push(ev.disclaimer);
            }
            return `BEGIN:VEVENT\nUID:${d.id}-${i}@passport\nDTSTAMP:${fmt(new Date())}\nDTSTART:${fmt(start)}\nDTEND:${fmt(end)}\nSUMMARY:${(s.icon || "❤")} ${escapeIcs(s.title)}\nDESCRIPTION:${escapeIcs(descParts.join(" — "))}\nLOCATION:${escapeIcs(location)}${ev?.ticket_url ? `\nURL:${escapeIcs(ev.ticket_url)}` : ""}\nEND:VEVENT`;
        }).filter(Boolean);
        const ics = `BEGIN:VCALENDAR\nVERSION:2.0\nPRODID:-//Passport//TravelDate//EN\n${events.join("\n")}\nEND:VCALENDAR`;
        const blob = new Blob([ics], { type: "text/calendar" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = `${(d.title || "traveldate").replace(/[^a-z0-9]+/gi, "-")}.ics`;
        document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
        toast.success(t("travelDateDetail.toasts.calDownloaded"));
    };

    const openMap = () => {
        const q = encodeURIComponent(`${d.title || ""} ${d.city}${d.country ? ", " + d.country : ""}`.trim());
        window.open(`https://www.google.com/maps/search/?api=1&query=${q}`, "_blank", "noopener,noreferrer");
    };

    return (
        <div className="min-h-screen grain" data-testid="traveldate-detail">
            <header className="sticky top-0 z-40 glass border-b" style={{ borderColor: "rgba(244,162,143,0.15)" }}>
                <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between gap-3">
                    <Link to="/traveldate" className="flex items-center gap-3">
                        <ArrowLeft size={16} color={CORAL} />
                        <PassportLogo size={26} />
                        <div className="hidden sm:block leading-none">
                            <div className="font-serif text-base text-white">TravelDate™</div>
                            <div className="text-[9px] tracking-[0.3em] uppercase" style={{ color: CORAL }}>{t("travelDateDetail.dateDetail")}</div>
                        </div>
                    </Link>
                    <div className="flex items-center gap-2">
                        <button onClick={regenerate} disabled={busy} className="btn-ghost text-xs !py-2 !px-3" data-testid="td-regenerate-top">
                            <ArrowsClockwise size={12} /> {t("travelDateDetail.regenerateTop")}
                        </button>
                    </div>
                </div>
            </header>

            <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-8">
                {/* Hero */}
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="rounded-3xl overflow-hidden relative aspect-[16/9] md:aspect-[16/7] mb-8" style={{ border: "1px solid rgba(244,162,143,0.25)" }}>
                    <img src={d.hero_image} alt={d.title} className="absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(11,19,32,0.2) 0%, rgba(5,10,17,0.92) 100%)" }} />
                    <div className="relative h-full p-6 md:p-10 flex flex-col justify-end">
                        <div className="flex flex-wrap items-center gap-3 mb-3">
                            <TierBadge tier={d.tier} />
                            <span className="text-[10px] tracking-[0.3em] uppercase text-white/70">{t(`travelDateDetail.tiers.${d.tier}`, d.tier_label)}</span>
                            <span className="rounded-full px-3 py-1 text-[10px] tracking-[0.2em] uppercase font-medium" style={{ background: "rgba(11,19,32,0.7)", color: CORAL, border: `1px solid ${CORAL}66`, backdropFilter: "blur(6px)" }}>
                                {t("travelDateDetail.score", { score: d.score })}
                            </span>
                            <StatusPill status={d.status} />
                        </div>
                        <h1 className="font-serif text-3xl sm:text-5xl md:text-6xl text-white leading-[1] tracking-tight" data-testid="td-title">❤️ {d.title}</h1>
                        <div className="mt-3 flex flex-wrap gap-3 text-white/85 text-sm">
                            <span className="inline-flex items-center gap-1"><MapPin size={12} weight="fill" color={CORAL} /> {d.city}{d.country ? `, ${d.country}` : ""}</span>
                            <span className="inline-flex items-center gap-1"><Clock size={12} weight="fill" color={CORAL} /> {d.duration_hours}h</span>
                            {d.estimated_cost_usd ? <span>· ~${d.estimated_cost_usd}</span> : null}
                        </div>
                    </div>
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8">
                    <div>
                        {d.vibe && <div className="text-lg text-white/85 italic mb-3">"{d.vibe}"</div>}
                        {d.why_this_works && <p className="text-white/75 mb-6 leading-relaxed">{d.why_this_works}</p>}

                        {/* Action bar */}
                        <div className="flex flex-wrap gap-2 mb-8" data-testid="td-actions">
                            <ActionBtn onClick={toggleFav} testId="td-save" icon={<Heart size={12} weight={d.favorited ? "fill" : "regular"} color={d.favorited ? CORAL : "white"} />}>
                                {d.favorited ? t("travelDateDetail.saved") : t("travelDateDetail.saveDate")}
                            </ActionBtn>
                            <ActionBtn onClick={() => setShareOpen(true)} testId="td-share-open" icon={<Share size={12} />}>{t("travelDateDetail.shareDate")}</ActionBtn>
                            <ActionBtn onClick={shareInMessages} testId="td-send-open" icon={<Sparkle size={12} />}>Send in Messages</ActionBtn>
                            <ActionBtn onClick={addToCalendar} testId="td-calendar" icon={<CalendarPlus size={12} />}>{t("travelDateDetail.addToCalendar")}</ActionBtn>
                            <ActionBtn onClick={addToTrip} disabled={busy} testId="td-add-to-trip" icon={<Suitcase size={12} />}>{t("travelDateDetail.addToTrip")}</ActionBtn>
                            <ActionBtn onClick={openMap} testId="td-view-map" icon={<MapTrifold size={12} />}>{t("travelDateDetail.viewMap")}</ActionBtn>
                            <ActionBtn onClick={regenerate} disabled={busy} testId="td-regenerate" icon={<ArrowsClockwise size={12} />}>{t("travelDateDetail.regenerateTop")}</ActionBtn>
                            <ActionBtn onClick={editPreferences} testId="td-edit-preferences" icon={<PencilSimple size={12} />}>{t("travelDateDetail.editPreferences")}</ActionBtn>
                        </div>

                        <div className="overline mb-4">{t("travelDateDetail.dateItinerary")}</div>
                        <ItineraryList d={d} />

                        {/* Total & budget-fit summary */}
                        {(d.estimated_cost_usd != null) && (
                            <div className="mt-5 rounded-2xl p-4 flex items-center justify-between" style={{ background: "var(--navy-deep)", border: `1px solid ${CORAL}22` }} data-testid="td-total-cost">
                                <div>
                                    <div className="text-[10px] tracking-[0.2em] uppercase text-white/50">{t("travelDateDetail.totalEstCost")}</div>
                                    <div className="font-serif text-2xl text-white mt-1">${d.estimated_cost_usd}</div>
                                </div>
                                <BudgetFitBadge date={d} />
                            </div>
                        )}

                        {/* Status controls */}
                        <div className="mt-8">
                            <div className="overline mb-2">{t("travelDateDetail.status")}</div>
                            <div className="flex flex-wrap gap-2">
                                {["planned", "completed"].map((s) => (
                                    <button key={s} onClick={() => setStatus(s)} className="text-[11px] px-3 py-1.5 rounded-full capitalize" style={{ background: d.status === s ? "rgba(244,162,143,0.18)" : "rgba(255,255,255,0.04)", color: d.status === s ? CORAL : "rgba(255,255,255,0.7)", border: `1px solid ${d.status === s ? CORAL + "66" : "rgba(255,255,255,0.08)"}` }} data-testid={`td-status-${s}`}>{t(`travelDateDetail.statuses.${s}`, s)}</button>
                                ))}
                                <button onClick={remove} className="text-[11px] px-3 py-1.5 rounded-full ml-auto" style={{ background: "rgba(255,120,120,0.14)", color: "#FF8888", border: "1px solid rgba(255,120,120,0.3)" }} data-testid="td-delete">
                                    <Trash size={11} className="inline mr-1" /> {t("travelDateDetail.delete")}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Score sidebar */}
                    <div className="space-y-6 lg:sticky lg:top-24 self-start">
                        <ScoreCard d={d} />
                        <div className="rounded-3xl p-4 text-[12px] text-white/60" style={{ background: "var(--navy-deep)", border: `1px solid ${CORAL}22` }}>
                            <div className="flex items-center gap-2 mb-2 text-white">
                                <Sparkle size={12} weight="fill" color="#D4AF37" /> {t("travelDateDetail.conciergeBox.title")}
                            </div>
                            <p>{t("travelDateDetail.conciergeBox.descPart1")}<Link to="/concierge" className="underline" style={{ color: "#D4AF37" }}>{t("travelDateDetail.conciergeBox.link")}</Link>{t("travelDateDetail.conciergeBox.descPart2")}</p>
                        </div>
                    </div>
                </div>
            </div>

            <AnimatePresence>
                {shareOpen && <ShareModal date={d} onClose={() => setShareOpen(false)} onUpdate={(patch) => setD({ ...d, ...patch })} />}
            </AnimatePresence>
        </div>
    );
}

function ActionBtn({ onClick, disabled, testId, icon, children }) {
    return (
        <button onClick={onClick} disabled={disabled} className="text-xs px-3 py-2 rounded-full inline-flex items-center gap-1.5 transition-all disabled:opacity-50"
            style={{ background: "rgba(244,162,143,0.10)", border: `1px solid ${CORAL}44`, color: "white" }} data-testid={testId}>
            {icon} {children}
        </button>
    );
}

export function BudgetFitBadge({ date }) {
    const { t } = useTranslation();
    const p = date.preferences || {};
    const target = p.budget;
    const total = date.estimated_cost_usd || 0;
    if (target == null) return null;
    const under = total <= target;
    return (
        <div className="text-right">
            <div className="text-[10px] tracking-[0.2em] uppercase text-white/50">{t("travelDateDetail.yourBudget")}</div>
            <div className={`text-sm font-medium mt-1 inline-flex items-center gap-1`} style={{ color: under ? "#9FD5D0" : "#F4A28F" }} data-testid="td-budget-fit">
                {under ? t("travelDateDetail.withinBudget", { target }) : t("travelDateDetail.overBudget", { diff: total - target, target })}
                {!under && <WarningCircle size={12} weight="fill" color="#F4A28F" />}
            </div>
        </div>
    );
}

export function ItineraryList({ d }) {
    const { t } = useTranslation();
    const stops = d.itinerary || [];
    const hasDayMarkers = stops.some((s) => s.day && s.day > 1);
    if (!hasDayMarkers) {
        return (
            <div className="space-y-3">
                {stops.map((s, i) => <StopRow key={s.id || i} s={s} i={i} />)}
            </div>
        );
    }
    // Group by day for weekend dates
    const day1 = stops.filter((s) => (s.day || 1) === 1);
    const day2 = stops.filter((s) => (s.day || 1) === 2);
    return (
        <div className="space-y-6">
            <div>
                <div className="text-[10px] tracking-[0.3em] uppercase mb-2" style={{ color: CORAL }}>{t("travelDateDetail.day1")}</div>
                <div className="space-y-3">{day1.map((s, i) => <StopRow key={s.id || i} s={s} i={i} />)}</div>
            </div>
            <div>
                <div className="text-[10px] tracking-[0.3em] uppercase mb-2" style={{ color: CORAL }}>{t("travelDateDetail.day2")}</div>
                <div className="space-y-3">{day2.map((s, i) => <StopRow key={s.id || i + 100} s={s} i={i + day1.length} />)}</div>
            </div>
        </div>
    );
}

export function StopRow({ s, i }) {
    const { t } = useTranslation();
    const Icon = KIND_ICON[s.kind] || KIND_ICON.default;
    const ev = s.event;
    return (
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
            className="rounded-2xl p-4 relative" style={{ background: "var(--navy-deep)", border: `1px solid ${s.over_budget ? "#F4A28F55" : ev ? "#F4A28F44" : "rgba(255,255,255,0.06)"}` }} data-testid={`td-stop-${i}`}>
            <div className="flex items-start gap-4">
                <div className="text-[11px] uppercase tracking-[0.15em] shrink-0" style={{ color: CORAL, minWidth: 62 }}>{s.time}</div>
                <div className="w-10 h-10 rounded-2xl grid place-items-center shrink-0 text-lg" style={{ background: "rgba(244,162,143,0.12)", border: `1px solid ${CORAL}44` }}>
                    {s.icon || <Icon size={14} color={CORAL} />}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-white font-medium leading-tight flex items-center gap-2 flex-wrap">
                        <span>{s.title}</span>
                        {ev && <span className="text-[9px] tracking-[0.2em] uppercase px-2 py-0.5 rounded-full inline-flex items-center gap-1" style={{ background: "rgba(212,175,55,0.14)", color: "#D4AF37", border: "1px solid rgba(212,175,55,0.4)" }}><Ticket size={10} weight="fill" /> {t("travelDateDetail.eventBadge")}</span>}
                        {s.over_budget && <span className="text-[9px] tracking-[0.2em] uppercase px-2 py-0.5 rounded-full" style={{ background: "rgba(244,162,143,0.16)", color: CORAL, border: `1px solid ${CORAL}66` }}>{t("travelDateDetail.overBudgetBadge")}</span>}
                    </div>
                    {s.subtitle && <div className="text-white/60 text-[13px] mt-1">{s.subtitle}</div>}
                    <div className="text-[10px] tracking-[0.15em] uppercase text-white/40 mt-1.5">
                        {s.duration_minutes} min{s.est_cost_usd ? ` · ~$${s.est_cost_usd}` : ""}
                    </div>
                </div>
            </div>
            {ev && <EventCard ev={ev} />}
        </motion.div>
    );
}

export function EventCard({ ev }) {
    const { t } = useTranslation();
    const isAi = ev.source === "ai";
    return (
        <div className="mt-3 rounded-2xl p-3.5 relative" style={{ background: "rgba(212,175,55,0.06)", border: "1px solid rgba(212,175,55,0.28)" }} data-testid="td-event-card">
            {ev.hero_image && (
                <div className="rounded-xl overflow-hidden mb-3 aspect-[16/7] bg-cover bg-center" style={{ backgroundImage: `url(${ev.hero_image})` }} />
            )}
            <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="min-w-0">
                    <div className="text-[10px] tracking-[0.25em] uppercase" style={{ color: "#D4AF37" }}>
                        {isAi ? t("travelDateDetail.aiSuggestion") : t("travelDateDetail.ticketmaster")} · {ev.category || "event"}
                    </div>
                    <div className="text-white font-serif text-base mt-1 leading-tight">{ev.name}</div>
                </div>
                {ev.price_display && (
                    <div className="text-[10px] tracking-[0.2em] uppercase px-2 py-1 rounded-full" style={{ background: "rgba(255,255,255,0.05)", color: "white", border: "1px solid rgba(255,255,255,0.12)" }}>
                        {ev.price_display}
                    </div>
                )}
            </div>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-white/70">
                {(ev.date_iso || ev.date_hint) && <div className="flex items-center gap-1.5"><CalendarPlus size={11} color={CORAL} /> {ev.date_iso || ev.date_hint}</div>}
                {(ev.start_time || ev.end_time) && <div className="flex items-center gap-1.5"><Clock size={11} color={CORAL} /> {[ev.start_time, ev.end_time].filter(Boolean).join(" – ")}</div>}
                {ev.venue && <div className="flex items-center gap-1.5"><MapPin size={11} color={CORAL} /> {ev.venue}{ev.address ? ` · ${ev.address}` : ""}</div>}
                {ev.distance_km != null && <div className="flex items-center gap-1.5"><MapTrifold size={11} color={CORAL} /> ~{Math.round(ev.distance_km)} km away</div>}
                {ev.rating != null && <div className="flex items-center gap-1.5"><Star size={11} weight="fill" color="#D4AF37" /> {ev.rating}{ev.review_count ? ` (${ev.review_count})` : ""}</div>}
            </div>
            {ev.description && <p className="text-white/80 text-[12px] mt-2 leading-relaxed">{ev.description}</p>}
            <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
                {ev.ticket_url ? (
                    <a href={ev.ticket_url} target="_blank" rel="noopener noreferrer" className="text-xs px-3 py-1.5 rounded-full font-semibold inline-flex items-center gap-1" style={{ background: "#D4AF37", color: "var(--navy)" }} data-testid="td-event-tickets">
                        <Ticket size={11} weight="fill" /> {t("travelDateDetail.getTickets")}
                    </a>
                ) : (
                    <button
                        onClick={() => window.open(`https://www.google.com/search?q=${encodeURIComponent(ev.name + " " + (ev.venue || ev.city || ""))}`, "_blank", "noopener,noreferrer")}
                        className="text-xs px-3 py-1.5 rounded-full inline-flex items-center gap-1"
                        style={{ background: "rgba(255,255,255,0.06)", color: "white", border: "1px solid rgba(255,255,255,0.14)" }}
                        data-testid="td-event-search-tickets"
                    >
                        <Ticket size={11} /> {t("travelDateDetail.searchTickets")}
                    </button>
                )}
                {isAi && ev.disclaimer && (
                    <div className="text-[10px] tracking-[0.15em] uppercase inline-flex items-center gap-1 px-2 py-1 rounded-full" style={{ background: "rgba(244,162,143,0.12)", color: CORAL, border: `1px solid ${CORAL}55` }} data-testid="td-event-disclaimer">
                        <WarningCircle size={11} weight="fill" /> {ev.disclaimer}
                    </div>
                )}
            </div>
        </div>
    );
}

function escapeIcs(s) {
    return String(s || "").replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\n/g, "\\n");
}

function ComposingScreen({ city }) {
    const { t } = useTranslation();
    const lines = t("travelDateDetail.composing.lines", { returnObjects: true }) || [];
    const [i, setI] = useState(0);
    const lineCount = lines.length;
    useEffect(() => {
        if (!lineCount) return;
        const timer = setInterval(() => setI((v) => (v + 1) % lineCount), 2200);
        return () => clearInterval(timer);
    }, [lineCount]);
    return (
        <div className="min-h-screen grid place-items-center px-6" data-testid="td-composing-screen">
            <div className="text-center max-w-md">
                <motion.div
                    animate={{ scale: [1, 1.15, 1], rotate: [0, 8, -8, 0] }} transition={{ duration: 2, repeat: Infinity }}
                    className="mx-auto w-16 h-16 rounded-full grid place-items-center"
                    style={{ background: "rgba(244,162,143,0.14)", border: `1px solid ${CORAL}77` }}
                >
                    <Heart size={30} weight="fill" color={CORAL} />
                </motion.div>
                <div className="font-serif text-3xl text-white mt-6">{t("travelDateDetail.composing.title")}</div>
                <div className="text-white/60 text-sm mt-2 min-h-[20px]" data-testid="td-composing-line">{lines[i] || `Composing the perfect ${city || "date"} itinerary…`}</div>
                <div className="text-white/40 text-[11px] tracking-[0.2em] uppercase mt-6">{t("travelDateDetail.composing.subtitle")}</div>
            </div>
        </div>
    );
}

function FailedScreen({ msg, onRetry }) {
    const { t } = useTranslation();
    return (
        <div className="min-h-screen grid place-items-center px-6" data-testid="td-failed-screen">
            <div className="text-center max-w-md">
                <div className="mx-auto w-16 h-16 rounded-full grid place-items-center" style={{ background: "rgba(255,120,120,0.14)", border: "1px solid rgba(255,120,120,0.4)" }}>
                    <WarningCircle size={30} weight="fill" color="#FF8888" />
                </div>
                <div className="font-serif text-3xl text-white mt-6">{t("travelDateDetail.failed.title")}</div>
                <div className="text-white/60 text-sm mt-2">{msg || t("travelDateDetail.failed.defaultMsg")}</div>
                <button onClick={onRetry} className="mt-6 px-6 py-2.5 rounded-full font-semibold" style={{ background: CORAL, color: "var(--navy)" }} data-testid="td-failed-retry">
                    <ArrowsClockwise size={12} weight="bold" className="inline mr-1" /> {t("travelDateDetail.failed.tryAgain")}
                </button>
            </div>
        </div>
    );
}


export function ScoreCard({ d }) {
    const { t } = useTranslation();
    const b = d.score_breakdown || {};
    const rows = [
        [t("travelDateDetail.scoreCard.romantic"), b.romantic_atmosphere],
        [t("travelDateDetail.scoreCard.quality"), b.quality],
        [t("travelDateDetail.scoreCard.uniqueness"), b.uniqueness],
        [t("travelDateDetail.scoreCard.coupleFit"), b.couple_suitability],
        [t("travelDateDetail.scoreCard.value"), b.value],
    ];
    return (
        <div className="rounded-3xl p-5" style={{ background: "var(--navy-deep)", border: `1px solid ${CORAL}44` }} data-testid="td-score-card">
            <div className="overline mb-2" style={{ color: CORAL }}>{t("travelDateDetail.scoreCard.title")}</div>
            <div className="flex items-baseline gap-2 mb-3">
                <div className="font-serif text-5xl" style={{ color: CORAL }}>{d.score}</div>
                <div className="text-white/50 text-xl">/100</div>
            </div>
            <TierBadge tier={d.tier} size={14} />
            <div className="mt-4 space-y-2">
                {rows.map(([label, v]) => (
                    <div key={label} className="text-[12px]">
                        <div className="flex justify-between text-white/70">
                            <span>{label}</span>
                            <span>{v ?? "—"}</span>
                        </div>
                        <div className="h-1 mt-1 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.06)" }}>
                            <div className="h-full" style={{ width: `${Math.max(0, Math.min(100, v || 0))}%`, background: `linear-gradient(90deg, ${CORAL}, #F4D06F)` }} />
                        </div>
                    </div>
                ))}
            </div>
            <div className="mt-4 text-[10px] tracking-[0.2em] uppercase text-white/40 leading-relaxed">
                {t("travelDateDetail.scoreCard.disclaimer")}
            </div>
        </div>
    );
}

function StatusPill({ status }) {
    const { t } = useTranslation();
    const tone = status === "completed" ? { bg: "rgba(159,213,208,0.16)", fg: "#9FD5D0", b: "rgba(159,213,208,0.5)" }
        : status === "cancelled" ? { bg: "rgba(255,120,120,0.14)", fg: "#FF8888", b: "rgba(255,120,120,0.4)" }
        : { bg: "rgba(244,162,143,0.14)", fg: CORAL, b: "rgba(244,162,143,0.4)" };
    return <span className="inline-flex px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.25em]" style={{ background: tone.bg, color: tone.fg, border: `1px solid ${tone.b}` }} data-testid="td-status-pill">{t(`travelDateDetail.statuses.${status || "planned"}`, status || "planned")}</span>;
}

function ShareModal({ date, onClose, onUpdate }) {
    const { t } = useTranslation();
    const [busy, setBusy] = useState(false);
    const url = date.share_slug ? `${window.location.origin}/traveldate/share/${date.share_slug}` : "";
    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);
    const toggle = async (enable) => {
        setBusy(true);
        try {
            const r = await travelDateApi.share(date.id, enable);
            onUpdate({ share_enabled: !!r.share_enabled, share_slug: r.slug || date.share_slug });
            toast.success(enable ? t("travelDateDetail.toasts.linkEnabled") : t("travelDateDetail.toasts.linkDisabled"));
        } catch { toast.error(t("travelDateDetail.toasts.errShareToggle")); }
        finally { setBusy(false); }
    };
    const copy = async () => {
        try {
            if (navigator.clipboard && window.isSecureContext) { await navigator.clipboard.writeText(url); toast.success(t("travelDateDetail.toasts.copied")); return; }
            const ta = document.createElement("textarea"); ta.value = url; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta); toast.success(t("travelDateDetail.toasts.copied"));
        } catch { toast.error(t("travelDateDetail.toasts.copyFailed")); }
    };
    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="fixed inset-0 z-[80] flex items-center justify-center p-4" style={{ background: "rgba(5,10,17,0.85)", backdropFilter: "blur(8px)" }} data-testid="td-share-modal">
            <motion.div onClick={(e) => e.stopPropagation()} initial={{ y: 16 }} animate={{ y: 0 }} className="rounded-3xl max-w-md w-full p-7" style={{ background: "var(--navy)", border: `1px solid ${CORAL}55` }}>
                <div className="overline mb-2" style={{ color: CORAL }}>{t("travelDateDetail.shareModal.overline")}</div>
                <h3 className="font-serif text-2xl text-white mb-2">{t("travelDateDetail.shareModal.title")}</h3>
                <p className="text-white/60 text-sm mb-5">{t("travelDateDetail.shareModal.sub")}</p>
                {date.share_enabled && date.share_slug ? (
                    <>
                        <div className="rounded-full px-4 py-3 text-sm text-white overflow-x-auto whitespace-nowrap" style={{ background: "rgba(0,0,0,0.4)", border: `1px solid ${CORAL}44` }}>{url}</div>
                        <div className="flex gap-2 mt-3">
                            <button onClick={copy} className="btn-ghost flex-1 justify-center" data-testid="td-share-copy">{t("travelDateDetail.shareModal.copy")}</button>
                            <button onClick={() => toggle(false)} disabled={busy} className="btn-ghost flex-1 justify-center" data-testid="td-share-off">{t("travelDateDetail.shareModal.turnOff")}</button>
                        </div>
                    </>
                ) : (
                    <button onClick={() => toggle(true)} disabled={busy} className="w-full py-3 rounded-full text-sm font-semibold" style={{ background: CORAL, color: "var(--navy)" }} data-testid="td-share-enable">
                        {busy ? t("travelDateDetail.shareModal.generating") : t("travelDateDetail.shareModal.generateLink")}
                    </button>
                )}
                <button onClick={onClose} className="w-full mt-3 text-white/60 text-xs tracking-[0.2em] uppercase">{t("travelDateDetail.shareModal.close")}</button>
            </motion.div>
        </motion.div>
    );
}
